import { type EventBusKey, useEventBus } from "@vueuse/core";
import type { Moment } from "moment";

import { backendOld } from "~/api";
import { ModelType, OperationModelKind as ModelKind } from "~/shared/enums";
import type { Entity, Notification, Task } from "~/shared/types";
import { useDataStore, useEnvironmentStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { printOrCaptureMessage } from "~/utils/exceptionClient";

import type { Actions } from ".";

// Notification echoes reconcile optimistic read state; tenant echoes update the tenant store.
const NO_DEDUPE_CLASS_NAMES = new Set([ModelKind.NOTIFICATION, ModelKind.TENANT]);
const IMPLEMENTED_MODELS_WITH_DIFF: ReadonlySet<string> = new Set([
  ModelKind.CHAT,
  ModelKind.CHAT_MESSAGE,
  ModelKind.DARTBOARD,
]);

export const EVENT_BUS_TASK_UPDATED_WS: EventBusKey<{
  taskDuid: string;
}> = Symbol("EVENT_BUS_TASK_UPDATED_WS");

const invalidateTaskHistoryForUpdate = (taskDuid: string, newItem: Task) => {
  const dataStore = useDataStore();
  const task = dataStore.getTaskByDuidIfLoaded(taskDuid);
  if (!task) {
    return;
  }

  Object.entries(task).forEach(([key, value]) => {
    if (value !== newItem[key as keyof Task]) {
      dataStore.invalidateHistoryActions(ModelType.TASK, taskDuid, key);
    }
  });
};

const invalidateTaskHistoryForDelete = (taskDuid: string) => {
  const dataStore = useDataStore();
  dataStore.invalidateHistoryActions(ModelType.TASK, taskDuid);
};

export const getModelGettersMap = () => {
  const dataStore = useDataStore();
  return {
    [ModelKind.AUTOMATION]: dataStore.getAutomationByDuid,
    [ModelKind.ATTACHMENT]: dataStore.getAttachmentByDuidIfLoaded,
    [ModelKind.CHAT]: dataStore.getChatByDuidIfLoaded,
    [ModelKind.CHAT_MESSAGE]: dataStore.getChatMessageByDuidIfLoaded,
    [ModelKind.COMMENT]: dataStore.getCommentByDuidIfLoaded,
    [ModelKind.CONNECTOR]: dataStore.getConnectorByDuid,
    [ModelKind.CONNECTOR_AUTH]: dataStore.getConnectorAuthByDuid,
    [ModelKind.DARTBOARD]: dataStore.getDartboardByDuid,
    [ModelKind.DASHBOARD]: dataStore.getDashboardByDuid,
    [ModelKind.DOC]: dataStore.getDocByDuidIfLoaded,
    [ModelKind.FOLDER]: dataStore.getFolderByDuid,
    [ModelKind.FORM_FIELD]: dataStore.getFormFieldByDuid,
    [ModelKind.FORM]: dataStore.getFormByDuid,
    [ModelKind.LAYOUT]: dataStore.getLayoutByDuid,
    [ModelKind.NOTIFICATION]: dataStore.getNotificationByDuidIfLoaded,
    [ModelKind.OPTION]: dataStore.getOptionByDuid,
    [ModelKind.PROPERTY]: dataStore.getPropertyByDuid,
    [ModelKind.SKILL]: dataStore.getSkillByDuid,
    [ModelKind.SPACE]: dataStore.getSpaceByDuid,
    [ModelKind.STATUS]: dataStore.getStatusByDuid,
    [ModelKind.TASK_KIND]: dataStore.getTaskKindByDuid,
    [ModelKind.TASK]: dataStore.getTaskByDuidIfLoaded,
    [ModelKind.USER]: dataStore.getUserByDuid,
    [ModelKind.VIEW]: dataStore.getViewByDuid,
    [ModelKind.WEBHOOK]: dataStore.getWebhookByDuid,
  } as const;
};

type ModelUpdateDispatch = {
  dispatch(
    item: Partial<Entity> & { duid: string },
    extras?: { hardUpdate?: boolean; skipSummaryStatisticsAndRollup?: boolean }
  ): void;
}["dispatch"];

export const getModelUpdateDispatchMap = (): Record<string, ModelUpdateDispatch> => {
  const dataStore = useDataStore();
  const tenantStore = useTenantStore();
  const userStore = useUserStore();

  return {
    "create agent memory": dataStore.$createOrUpdateAgentMemory,
    "update agent memory": dataStore.$createOrUpdateAgentMemory,
    "delete agent memory": dataStore.$deleteAgentMemory,
    "create agent schedule": dataStore.$createOrUpdateAgentSchedule,
    "update agent schedule": dataStore.$createOrUpdateAgentSchedule,
    "delete agent schedule": dataStore.$deleteAgentSchedule,
    "create automation": dataStore.$createOrUpdateAutomation,
    "update automation": dataStore.$createOrUpdateAutomation,
    "delete automation": dataStore.$deleteAutomation,
    "create attachment": dataStore.$createOrUpdateAttachment,
    "update attachment": dataStore.$createOrUpdateAttachment,
    "delete attachment": dataStore.$deleteAttachment,
    "create chat": dataStore.$createChat,
    "update chat": dataStore.$updateChat,
    "delete chat": dataStore.$deleteChat,
    "create chat message": dataStore.$createChatMessage,
    "update chat message": dataStore.$updateChatMessage,
    "delete chat message": dataStore.$deleteChatMessage,
    "create comment": dataStore.$createOrUpdateComment,
    "update comment": dataStore.$createOrUpdateComment,
    "delete comment": dataStore.$deleteComment,
    "create connector": dataStore.$createOrUpdateConnector,
    "update connector": dataStore.$createOrUpdateConnector,
    "delete connector": dataStore.$deleteConnector,
    "create connector auth": dataStore.$createOrUpdateConnectorAuth,
    "update connector auth": dataStore.$createOrUpdateConnectorAuth,
    "delete connector auth": dataStore.$deleteConnectorAuth,
    "create dartboard": dataStore.$createDartboard,
    "update dartboard": dataStore.$updateDartboard,
    "delete dartboard": dataStore.$deleteDartboard,
    "create dashboard": dataStore.$createOrUpdateDashboard,
    "update dashboard": dataStore.$createOrUpdateDashboard,
    "delete dashboard": dataStore.$deleteDashboard,
    "create doc": dataStore.$createOrUpdateDoc,
    "update doc": dataStore.$createOrUpdateDoc,
    "delete doc": dataStore.$deleteDoc,
    "create event": dataStore.$createEvent,
    "create folder": dataStore.$createOrUpdateFolder,
    "update folder": dataStore.$createOrUpdateFolder,
    "delete folder": dataStore.$deleteFolder,
    "create form": dataStore.$createOrUpdateForm,
    "update form": dataStore.$createOrUpdateForm,
    "delete form": dataStore.$deleteForm,
    "create form field": dataStore.$createOrUpdateFormField,
    "update form field": dataStore.$createOrUpdateFormField,
    "delete form field": dataStore.$deleteFormField,
    "create layout": dataStore.$createOrUpdateLayout,
    "update layout": dataStore.$createOrUpdateLayout,
    "delete layout": dataStore.$deleteLayout,
    "create notification": dataStore.$createNotification,
    "update notification": dataStore.$updateNotification,
    "delete notification": dataStore.$deleteNotification,
    "create option": dataStore.$createOrUpdateOption,
    "update option": dataStore.$createOrUpdateOption,
    "delete option": dataStore.$deleteOption,
    "create property": dataStore.$createOrUpdateProperty,
    "update property": dataStore.$createOrUpdateProperty,
    "delete property": dataStore.$deleteProperty,
    "create skill": dataStore.$createOrUpdateSkill,
    "update skill": dataStore.$createOrUpdateSkill,
    "delete skill": dataStore.$deleteSkill,
    "create space": dataStore.$createOrUpdateSpace,
    "update space": dataStore.$createOrUpdateSpace,
    "delete space": dataStore.$deleteSpace,
    "create status": dataStore.$createOrUpdateStatus,
    "update status": dataStore.$createOrUpdateStatus,
    "delete status": dataStore.$deleteStatus,
    "create task": dataStore.$createOrUpdateTask,
    "update task": dataStore.$createOrUpdateTask,
    "delete task": dataStore.$deleteTask,
    "create task kind": dataStore.$createOrUpdateTaskKind,
    "update task kind": dataStore.$createOrUpdateTaskKind,
    "delete task kind": dataStore.$deleteTaskKind,
    "create text snapshot": dataStore.$createOrUpdateTextSnapshot,
    "update text snapshot": dataStore.$createOrUpdateTextSnapshot,
    "delete text snapshot": dataStore.$deleteTextSnapshot,
    "create tenant": tenantStore.setTenant,
    "delete tenant": userStore.forceLogout,
    "update tenant": tenantStore.setTenant,
    "create user": dataStore.$createOrUpdateUser,
    "update user": dataStore.$createOrUpdateUser,
    "delete user": dataStore.$deleteUser,
    "create view": dataStore.$createOrUpdateView,
    "update view": dataStore.$createOrUpdateView,
    "delete view": dataStore.$deleteView,
    "create webhook": dataStore.$createOrUpdateWebhook,
    "update webhook": dataStore.$createOrUpdateWebhook,
    "delete webhook": dataStore.$deleteWebhook,
  } as const;
};

/** Websockets manager */
const websockets = (actions: Actions) => ({
  getManager: () => {
    class WebsocketManager {
      endpoint!: string;

      socket!: WebSocket;

      closingIntentionally = false;

      constructor() {
        const environmentStore = useEnvironmentStore();
        this.endpoint = `${environmentStore.wsBase}/ws/v0/updates/`;
      }

      connect() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const thiz = this;

        const modelUpdateDispatchMap = getModelUpdateDispatchMap();

        thiz.closingIntentionally = false;
        thiz.socket = new WebSocket(thiz.endpoint);

        thiz.socket.onmessage = (event) => {
          const message = JSON.parse(event.data);
          if (message.type !== "model_update") {
            // eslint-disable-next-line no-console
            console.warn("Unexpected websocket message ", message);
            return;
          }

          // console.log(message.action, message.className, "incoming");

          if (
            !NO_DEDUPE_CLASS_NAMES.has(message.className) &&
            message.item.updatedByClientDuid === usePageStore().duid
          ) {
            return;
          }

          // console.log(message.action, message.className, "not deduped");

          // Invalidate undo/redo history
          if (message.className === ModelKind.TASK) {
            if (message.action === "delete") {
              invalidateTaskHistoryForDelete(message.item.duid);
            } else if (message.action === "update") {
              invalidateTaskHistoryForUpdate(message.item.duid, message.item);
            }

            useEventBus(EVENT_BUS_TASK_UPDATED_WS).emit({ taskDuid: message.item.duid });
          }

          const updateKey = `${message.action} ${message.className}` as keyof typeof modelUpdateDispatchMap;
          const updateFunc = modelUpdateDispatchMap[updateKey];
          if (!updateFunc) {
            printOrCaptureMessage("Unexpected websocket update key", { updateKey });
            return;
          }
          updateFunc(message.item, message.extras);
        };

        thiz.socket.onclose = () => {
          if (thiz.closingIntentionally) {
            return;
          }
          // oxlint-disable-next-line dart/no-set-timeout
          setTimeout(() => thiz.connect(), 5000);
        };

        thiz.socket.onerror = () => {
          thiz.socket.close();
        };
      }

      disconnect() {
        this.closingIntentionally = true;
        this.socket.close();
      }

      // eslint-disable-next-line class-methods-use-this
      async syncChangesSince(since: Moment) {
        const tenantStore = useTenantStore();
        const userStore = useUserStore();

        const modelUpdateDispatchMap = getModelUpdateDispatchMap();
        const modelGettersMap = getModelGettersMap();

        const response = await backendOld.getSyncChanges(since.toISOString());
        const { updated, deleted } = response.data;

        Object.entries(updated).forEach(([modelName, items]) => {
          if (!Array.isArray(items) || items.length === 0) {
            return;
          }

          const action = IMPLEMENTED_MODELS_WITH_DIFF.has(modelName) ? "create" : "update";
          const updateKey = `${action} ${modelName}` as keyof typeof modelUpdateDispatchMap;
          const updateFunc = modelUpdateDispatchMap[updateKey];

          if (!updateFunc) {
            // eslint-disable-next-line no-console
            console.warn(`No dispatch function for ${action} ${modelName}`);
            return;
          }

          items.forEach((item) => {
            if (modelName === ModelKind.TASK) {
              invalidateTaskHistoryForUpdate(item.duid, item);
            } else if (modelName === ModelKind.USER && item.duid === userStore.duid) {
              actions.app.setUser({ ...item, viewMode: userStore.viewMode });
            } else if (modelName === ModelKind.TENANT && item.duid === tenantStore.duid) {
              tenantStore.setTenant(item);
            } else if (
              modelName === ModelKind.NOTIFICATION &&
              Date.parse((item as Notification).createdAt) >= since.valueOf()
            ) {
              modelUpdateDispatchMap["create notification"](item);
              return;
            }

            updateFunc(item);
          });
        });

        Object.entries(deleted).forEach(([modelName, duids]) => {
          if (!Array.isArray(duids) || duids.length === 0) {
            return;
          }

          const deleteKey = `delete ${modelName}` as keyof typeof modelUpdateDispatchMap;
          const deleteFunc = modelUpdateDispatchMap[deleteKey];
          if (!deleteFunc) {
            printOrCaptureMessage("No websocket dispatch function", { functionName: `delete ${modelName}` });
            return;
          }

          const getKey = modelName as keyof typeof modelGettersMap;
          const getFunc = modelGettersMap[getKey];
          if (!getFunc) {
            printOrCaptureMessage("No websocket dispatch function", { functionName: `get ${modelName}` });
            return;
          }

          duids.forEach((duid: string) => {
            if (modelName === ModelKind.TASK) {
              invalidateTaskHistoryForDelete(duid);
            }

            const loadedItem = getFunc(duid);
            if (!loadedItem) {
              return;
            }

            deleteFunc(loadedItem);
          });
        });
      }
    }

    return new WebsocketManager();
  },
});

export type WebsocketManager = ReturnType<ReturnType<typeof websockets>["getManager"]>;

export default websockets;
